import { AlertTriangle, Info } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * AiDisclaimer — avertissement légal obligatoire sur toutes les pages
 * où l'IA produit du contenu juridique (Creator, Studio, Advice, LegalSearch, Contracts, Prediction).
 *
 * Essentiel pour :
 *  - Conformité au monopole de l'avocat (loi 71-1130 art. 4 et 54)
 *  - Limitation de la responsabilité professionnelle (RC Pro)
 *  - Information claire des utilisateurs (LCEN art. 6-III)
 *
 * Variants :
 *  - "compact" : petit rappel en bas de page
 *  - "full"    : encart complet au-dessus du contenu généré
 *  - "inline"  : petite bannière discrète en haut d'une page
 */
export default function AiDisclaimer({ variant = "compact", className = "" }) {
  if (variant === "full") {
    return (
      <div className={`rounded-lg border-2 border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-700 p-4 ${className}`}>
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <h3 className="font-semibold text-amber-900 dark:text-amber-200 mb-1">
              Avertissement — Contenu généré par IA
            </h3>
            <p className="text-amber-900 dark:text-amber-200/90 leading-relaxed">
              Les contenus produits sur cette page par l'intelligence artificielle <strong>ne constituent pas
              une consultation juridique</strong> au sens des articles 4, 54 et 55 de la loi n° 71-1130 du
              31 décembre 1971. Il s'agit d'un outil d'aide à la recherche et à la rédaction destiné aux
              professionnels du droit.
            </p>
            <p className="text-amber-900 dark:text-amber-200/90 mt-2 leading-relaxed">
              <strong>Vous restez seul responsable</strong> de la vérification, l'adaptation et la validation
              des contenus avant tout usage (dépôt au greffe, signature d'acte, communication au client).
            </p>
            <Link to="/cgu" className="text-xs text-amber-800 dark:text-amber-300 hover:underline inline-block mt-2">
              Voir les conditions d'utilisation →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className={`flex items-center gap-2 text-xs text-muted-foreground bg-muted/40 rounded-md px-3 py-2 ${className}`}>
        <Info className="h-3.5 w-3.5 flex-shrink-0" />
        <span>
          Contenu généré par IA — ne remplace pas l'avis d'un professionnel du droit.
          {" "}<Link to="/cgu" className="text-primary hover:underline">En savoir plus</Link>
        </span>
      </div>
    );
  }

  // compact (default)
  return (
    <div className={`text-xs text-muted-foreground text-center py-2 ${className}`}>
      <AlertTriangle className="inline-block h-3 w-3 mr-1 -mt-0.5" />
      Les contenus générés par IA ne constituent pas une consultation juridique.
      {" "}<Link to="/cgu" className="text-primary hover:underline">CGU</Link>
    </div>
  );
}
